import { UseFormValues } from "@formiz/core/dist/types/form.types";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Button, ButtonVariant, IconVariants, Text, TextVariants, useMedia } from "@next-platform/client-common-spicy-ui";
import { Image } from "@nextui-org/react";
import { useCallback, useRef, useState } from "react";

import AvatarGeneratorModal from "@/components/AvatarGeneratorModal";
import useAvatarUpload from "@/components/CharacterForm/useAvatarUpload";
import { Grid } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { AvatarPreset, FormTypes } from "@/types";
import { cn, getAvatarUrl } from "@/utils";

import AvatarWithModal from "../common/AvatarWithModal";

type AvatarCreateProps = {
  characterAvatar: string;
  setCharacterAvatar: (avatar: string) => void;
  disabled?: boolean;
  required?: boolean;
  persona: boolean;
  form?: UseFormValues;
  formType?: FormTypes;
};

const AvatarCreate = ({ characterAvatar, setCharacterAvatar, disabled, persona, required, form, formType }: AvatarCreateProps) => {
  const [stateIsAvatarModalVisible, setStateIsAvatarModalVisible] = useState(false);
  const { isDesktop } = useMedia();
  const refImagePicker = useRef<HTMLInputElement>(null);
  const { onImageUpload } = useAvatarUpload({ setCharacterAvatar, imagePickerRef: refImagePicker });
  const [stateIsZoomedAvatarModalOpen, setStateIsZoomedAvatarModalOpen] = useState(false);
  const isPersonaUploadAvatarAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.PersonaUploadAvatar);

  const onCloseHandler = useCallback(() => {
    setStateIsAvatarModalVisible(false);
  }, []);

  const onSaveHandler = useCallback(
    (key: string) => {
      if (refImagePicker.current) {
        refImagePicker.current.value = "";
      }
      setCharacterAvatar(key);
      setStateIsAvatarModalVisible(false);
    },
    [setCharacterAvatar],
  );

  const handleGenerateAvatar = useCallback(() => {
    setStateIsAvatarModalVisible(true);
  }, []);

  const handleOpenZoomedAvatar = useCallback(() => {
    setStateIsZoomedAvatarModalOpen(true);
  }, []);

  return (
    <div className="flex flex-col gap-2" data-testid="AvatarCreate">
      <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
        <Trans i18nKey="common:avatar" /> {!!required && <span className="text-red-10">*</span>}
      </Text>
      {characterAvatar ? (
        <Grid className={cn(persona ? undefined : "p-3")}>
          <AvatarWithModal
            id={characterAvatar}
            src={getAvatarUrl(characterAvatar, { preset: persona && isDesktop ? AvatarPreset.AVATAR_400X400 : AvatarPreset.AVATAR_256X256 }) || ""}
            isOpen={stateIsZoomedAvatarModalOpen}
            onOpenChange={setStateIsZoomedAvatarModalOpen}
          >
            <Image
              className="object-cover"
              alt="persona avatar image"
              src={getAvatarUrl(characterAvatar, { preset: persona && isDesktop ? AvatarPreset.AVATAR_400X400 : AvatarPreset.AVATAR_256X256 }) || ""}
              width={150}
              height={150}
              onClick={handleOpenZoomedAvatar}
            />
          </AvatarWithModal>
        </Grid>
      ) : null}
      <div className="flex flex-row gap-2">
        <Button
          data-testid="AvatarCreateGenerate"
          iconStart={IconVariants.SPARKLES}
          variant={ButtonVariant.SECONDARY}
          disabled={disabled}
          onClick={handleGenerateAvatar}
          aria-label="generate-avatar"
        >
          <Trans i18nKey="common:generate" />
        </Button>
        {isPersonaUploadAvatarAllowed ? (
          <>
            <input id="image-picker" type="file" accept="image/*" hidden ref={refImagePicker} data-testid="AvatarCreateUploadInput" />
            <Button data-testid="AvatarCreateUpload" iconStart={IconVariants.UPLOAD} variant={ButtonVariant.OUTLINED} onClick={onImageUpload}>
              <Trans i18nKey="common:upload" />
            </Button>
          </>
        ) : null}
      </div>
      <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
        {persona ? <Trans i18nKey="personas:form.avatar.helperText" /> : <Trans i18nKey="common:avatar.helperText" />}
      </Text>
      <AvatarGeneratorModal
        currentAvatarUrl={getAvatarUrl(characterAvatar, { preset: AvatarPreset.AVATAR_256X256 }) as string}
        isVisible={stateIsAvatarModalVisible}
        onClose={onCloseHandler}
        onSave={onSaveHandler}
        form={form}
        formType={formType}
        isAiPromptEnabled={true}
      />
    </div>
  );
};

export default AvatarCreate;
